import { Link } from "@tanstack/react-router";
import { ArrowLeft, Check, MapPin } from "lucide-react";
import type { ReactNode } from "react";
import { Logo } from "@/app/Logo";
import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { config } from "@/lib/config";
import { ThemeToggle } from "@/routes/-shared/StandaloneFrame";

export const SIGNUP_STEPS = ["Your email", "Confirm it's you", "Create a passkey", "Name your workspace", "Choose a plan"] as const;

export interface OnboardingFrameProps {
  /** Index into SIGNUP_STEPS, or null to hide progress (waitlist, welcome). */
  step: number | null;
  /** Wider content column, for the plan picker and the welcome screen. */
  wide?: boolean;
  /** Replaces the step list in the brand panel. */
  panel?: ReactNode;
  children: ReactNode;
}

/** The sign-up frame: a calm brand panel with progress on large screens, a slim progress bar on phones. */
export function OnboardingFrame({ step, wide, panel, children }: OnboardingFrameProps) {
  const total = SIGNUP_STEPS.length;
  return (
    <div className="grid min-h-dvh bg-canvas lg:grid-cols-[minmax(340px,420px)_minmax(0,1fr)]">
      <aside className="relative hidden overflow-hidden bg-[oklch(0.3_0.06_162)] lg:block" aria-label="Sign-up progress">
        <div aria-hidden="true" className="absolute inset-0 opacity-[0.16] [background-image:radial-gradient(oklch(1_0_0/0.6)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:linear-gradient(170deg,black,transparent_70%)]" />
        <div aria-hidden="true" className="absolute -bottom-48 -left-32 size-[520px] rounded-full bg-[oklch(0.55_0.12_162)] opacity-35 blur-3xl" />
        <div className="sticky top-0 flex min-h-dvh flex-col justify-between p-10 xl:p-12">
          <span className="inline-flex items-center gap-2">
            <svg viewBox="0 0 32 32" className="size-8" aria-hidden="true">
              <rect width="32" height="32" rx="8" fill="oklch(0.98 0.01 162)" />
              <path d="M21.6 11.1A7.2 7.2 0 1 0 23.2 16h-6.6" fill="none" stroke="oklch(0.3 0.06 162)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="font-display text-2xl font-semibold text-white">Guv</span>
          </span>
          <div className="my-12">
            {panel ?? (
              <>
                <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white">Set up in about two minutes.</h2>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/70">No password to invent and nothing to install. You can deploy straight after.</p>
                {step !== null && (
                  <ol className="mt-10 space-y-1">
                    {SIGNUP_STEPS.map((label, i) => {
                      const done = i < step;
                      const current = i === step;
                      return (
                        <li key={label} aria-current={current ? "step" : undefined} className="relative flex items-center gap-3.5 py-2">
                          {i < total - 1 && <span aria-hidden="true" className={cn("absolute left-[13px] top-[34px] h-[calc(100%-20px)] w-px", done ? "bg-white/45" : "bg-white/15")} />}
                          <span
                            className={cn(
                              "relative flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors duration-200",
                              done && "bg-white text-[oklch(0.3_0.06_162)]",
                              current && "bg-white/15 text-white ring-2 ring-white",
                              !done && !current && "text-white/55 ring-1 ring-white/25",
                            )}
                          >
                            {done ? <Check className="size-4" strokeWidth={3} aria-hidden="true" /> : i + 1}
                          </span>
                          <span className={cn("text-sm", current ? "font-semibold text-white" : done ? "text-white/80" : "text-white/55")}>
                            {label}
                            {done && <span className="sr-only"> (done)</span>}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </>
            )}
          </div>
          <p className="flex items-center gap-2 text-sm text-white/60">
            <MapPin className="size-4" aria-hidden="true" /> Hosted in the UK. Priced in pounds.
          </p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between px-4 sm:px-6">
          <Link to="/login" className="rounded-md lg:invisible" aria-label="Guv">
            <Logo />
          </Link>
          <ThemeToggle />
        </header>
        {step !== null && (
          <div className="px-4 sm:px-6 lg:hidden">
            <div className="mx-auto flex max-w-md items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-surface-3" aria-hidden="true">
                <div className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out" style={{ width: `${((step + 1) / total) * 100}%` }} />
              </div>
              <span className="shrink-0 text-xs font-medium text-muted tabular-nums">
                Step {step + 1} of {total}
                <span className="sr-only">: {SIGNUP_STEPS[step]}</span>
              </span>
            </div>
          </div>
        )}
        <main id="main" className="flex flex-1 justify-center px-4 pb-16 pt-8 sm:px-6 lg:items-center lg:pt-4">
          <div className={cn("w-full", wide ? "max-w-4xl" : "max-w-md")}>{children}</div>
        </main>
        <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 pb-6 text-xs text-subtle">
          <a href={`${config.docsBase}/legal/privacy`} className="rounded-sm hover:text-text">
            Privacy
          </a>
          <a href={`${config.docsBase}/legal/terms`} className="rounded-sm hover:text-text">
            Terms
          </a>
          <span>Made and hosted in the UK</span>
        </footer>
      </div>
    </div>
  );
}

/** Page heading used by every step, so they read as one flow. */
export function StepHeading({ eyebrow, title, children }: { eyebrow?: ReactNode; title: ReactNode; children?: ReactNode }) {
  return (
    <div>
      {eyebrow && <p className="mb-2 text-sm font-medium text-accent-text">{eyebrow}</p>}
      <h1 className="font-display text-3xl font-semibold tracking-tight text-text text-balance">{title}</h1>
      {children && <div className="mt-2 text-md leading-relaxed text-muted">{children}</div>}
    </div>
  );
}

/** "Back" for steps that can be revisited. */
export function BackButton({ onClick, children = "Back" }: { onClick: () => void; children?: ReactNode }) {
  return (
    <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={onClick} className="-ml-2 mb-5">
      {children}
    </Button>
  );
}
